import { EntityStatus } from '../../../../../entities/entity.status/entity.status';
import { Party } from '../../../../../entities/entity.status/enum';
import { WorkOrder } from '../../../../../entities/job/workorder';
import { EmbeddedCodeLike, resolveCodeCapability } from '../../../../../entities/shared/code';
import { canEditItemInStatus, hasAuthorWindow } from '../../../../../entities/shared/item.status.rules';
import { canEditInStatus } from '../../../../../entities/tasks/task.status.policy';
import { stageIsOpenForContractorItems } from '../stage.claim.state';
import { StageItemSecurityCapabilities } from './stage.item.security.capabilities';

/** What the contractor portal knows about the caller and the stage before it looks at a line. */
export interface ContractorItemScope {
    stage: WorkOrder | null | undefined;
    /** The tenant holds the Claims add-on; without it the portal has never let a contractor touch a line. */
    claimAddOnEnabled: boolean;
    /** The subcontractor company the caller is acting for: the Draft window compares it to the line's author. */
    companyId: string;
}

export const CONTRACTOR_ITEM_CAPABILITIES_NONE: StageItemSecurityCapabilities = Object.freeze({
    canCreate: false, canUpdate: false, canUpdateNote: false, canDelete: false, canCancel: false, canMove: false,
    canApprove: false, canViewPricing: true, canModifyPricing: false, canModifyLockedItems: false, canManageMedia: false,
});

/**
 * The contractor portal's item rule as one capability set, in the shape the web board and the item
 * page already read.
 *
 * A contractor holds no Jobs right, so the role resolver can never say yes for them; this one answers
 * from the stage's claim state and the line's status instead, ANDed with the same line-level terms
 * (`isReadonly`, the residual allow flags, finance links) the role resolver applies.
 *
 * Office lines (`codes`) are always read-only here. A pending line the status set no longer resolves is
 * locked rather than open: the portal's window was Draft-only before this rule existed, and a line
 * stamped Requested under the old flow must not reopen because its status went missing.
 *
 * Request is not a capability of its own: the portal offers it exactly when `canUpdate` holds, since
 * both mean "this is my Draft and the stage is still mine to change".
 */
export function resolveContractorItemCapabilities(scope: ContractorItemScope, item: EmbeddedCodeLike | null | undefined, status: EntityStatus | undefined): StageItemSecurityCapabilities {

    const canCreate = stageIsOpenForContractorItems(scope.stage, scope.claimAddOnEnabled);
    const ownPendingLine = !!item?.isClaimCode && !!status && canEditItemInStatus(status, Party.Contractor, item.raisedBy, scope.companyId);
    const canUpdate = canCreate && ownPendingLine && resolveCodeCapability(item, 'edit');
    const canDelete = canCreate && ownPendingLine && resolveCodeCapability(item, 'delete') && !item?.purchaseOrder?._id && !item?.claimImport;

    return {
        ...CONTRACTOR_ITEM_CAPABILITIES_NONE, canCreate, canUpdate, canUpdateNote: canUpdate, canDelete,
        ...(canUpdate ? {} : { updateRefusal: contractorItemRefusal(canCreate, item, status, scope.companyId, 'update') }),
        ...(canDelete ? {} : { deleteRefusal: contractorItemRefusal(canCreate, item, status, scope.companyId, 'delete') }),
    };
}

/** The words a bulk run shows for a line it leaves out, per action: a delete names deleting, a change names its own verbs. */
const REFUSAL_WORDS = {
    update: { office: 'Already approved by the office', status: 'only the office can change it', author: 'request it' },
    delete: { office: 'Already approved by the office — only the office can delete it', status: 'only the office can delete it', author: 'delete it' },
} as const;

/** Why the contractor may not change (or delete) the line: the office line first, then the stage, the status, its author, then what only blocks a delete, and the line lock. */
function contractorItemRefusal(stageOpen: boolean, item: EmbeddedCodeLike | null | undefined, status: EntityStatus | undefined, companyId: string, action: keyof typeof REFUSAL_WORDS): string {

    const words = REFUSAL_WORDS[action];
    if (!item?.isClaimCode) return words.office;
    if (!stageOpen) return 'This stage no longer takes changes from you';
    if (!status) return 'This line is locked';
    if (status.editableBy && !canEditInStatus(status, Party.Contractor)) return `Its status is ${status.name} — ${words.status}`;
    if (hasAuthorWindow(status) && !canEditItemInStatus(status, Party.Contractor, item.raisedBy, companyId)) {
        return item.raisedBy?.party === Party.Office ? `Added by the office — only the office can ${words.author}` : `Added by someone else — only they can ${words.author}`;
    }
    if (action === 'delete' && item.purchaseOrder?._id) return 'It is on a purchase order';
    if (action === 'delete' && item.claimImport) return 'It came from a claim import';

    return 'This line is locked';
}
